/**
 * Sizing, text styles and responsive layouts shared by the competency-matrix report page. The
 * design is the Lovable prototype (`performance-ai5/client/pages/Competencies.tsx` and its
 * `CompetencyMatrix.tsx`/`KPICard.tsx`/`LegendLevels.tsx`). These named constants exist so the
 * page and the neighbouring report pages (`ThematicMeetings/components/meetingStyles.ts`,
 * `Compensation/compensationStyles.ts`) cannot drift apart, not to correct the scale.
 */

/** Page title: 24px medium, matching `MEETINGS_TITLE` in the neighbouring feature. */
export const REPORT_TITLE = 'text-[24px] font-medium';

/** Highlight card's big number/name (`HighlightCard`): 24px bold, per the Lot C spec. */
export const HIGHLIGHT_VALUE = 'text-navy text-[24px] font-bold';

/** Matrix and legend section titles: 18px semibold (`CompetencyMatrix.tsx:84` in the prototype —
 *  `text-lg`, drawn at a 16px root). */
export const REPORT_SECTION_TITLE = 'text-navy text-[18px] font-semibold';

/**
 * The three column widths below come in pairs: a Tailwind class for the layout, and the same
 * number for the arrow-scroll arithmetic in `CompetencyMatrix`, which has to subtract the two
 * sticky columns from the visible width. The literal is repeated rather than interpolated into
 * the class because Tailwind only emits a utility it can read whole in the source.
 */

/** Sticky label column (group/competency names): 240px (`CompetencyMatrix.tsx:90` in the
 *  prototype — `md:w-60`, at a 16px root). */
export const MATRIX_LABEL_COLUMN_WIDTH = 'w-[240px]';
export const MATRIX_LABEL_COLUMN_WIDTH_PX = 240;

/** One employee column: 120px (`CompetencyMatrix.tsx:66` in the prototype — desktop
 *  `COLUMN_WIDTH`). */
export const MATRIX_EMPLOYEE_COLUMN_WIDTH = 'w-[120px]';
export const MATRIX_EMPLOYEE_COLUMN_WIDTH_PX = 120;

/** Gap between employee columns: 12px (`CompetencyMatrix.tsx:67` in the prototype — desktop
 *  `GAP`). */
export const MATRIX_COLUMN_GAP = 'gap-x-[12px]';

/** Sticky "Average" column. Not a prototype value: the prototype reserves an 80px slot for a
 *  single number ("4.3"), but this report's average also carries the scale ("3,9 / 5"), so it
 *  gets its own, slightly wider, literal width instead of reusing that narrower one. */
export const MATRIX_AVERAGE_COLUMN_WIDTH = 'w-[96px]';
export const MATRIX_AVERAGE_COLUMN_WIDTH_PX = 96;

/** Row min-height, wide enough for a `CustomPill` without clipping. Not a prototype value: the
 *  prototype's rows are unconstrained flex rows with their own internal padding. */
export const MATRIX_ROW_MIN_HEIGHT = 'min-h-[56px]';

/** Job title under an employee's name: 11px, one step below the name's `text-xs`, which the
 *  Tailwind scale has no token for. */
export const MATRIX_EMPLOYEE_JOB_TITLE = 'text-[11px]';

/**
 * The two responsive layouts below are written with `max-*` variants instead of the usual
 * "base class + `bp:` override" pair. `app.css` imports the library's compiled sheet after
 * Tailwind, and both emit into `@layer utilities`, so a library-emitted base utility (`.flex-col`
 * and `.w-full` both are) sits later in the layer than our breakpoint override and wins at every
 * width — silently killing the override. Stating only the `max-*` half avoids the collision.
 */

/** Toolbar: title block above the filters below `lg`, side by side from `lg` up. */
export const REPORT_TOOLBAR_LAYOUT = 'flex gap-4 max-lg:flex-col lg:items-start lg:justify-between';

/** Competency-profile selector in the toolbar: full width below `sm`, 220px from `sm` up. */
export const REPORT_PROFILE_SELECT_WIDTH = 'max-sm:w-full sm:w-[220px]';
